import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type Cifras = components['schemas']['CifrasRead']
export type Cifra = components['schemas']['CifraRead']
export type VigenciaSve = components['schemas']['VigenciaSveRead']
export type AlcanceSve = components['schemas']['AlcanceRead']

/** La misma clave que invalida el modal del punto de uso. */
export function useCifras(businessId: string | undefined) {
  return useQuery({
    queryKey: ['cifras', businessId],
    queryFn: async () => (await api.get<Cifras>(`/businesses/${businessId}/cifras`)).data,
    enabled: !!businessId,
  })
}

/** R6: una por cargo (40-70 en una constructora); la clave cuelga de `['cifras', businessId]` para heredar
 * su invalidación. */
const VIGENCIAS_PAGE_SIZE = 20

export function useVigenciasSve(businessId: string | undefined, page = 1) {
  return usePagedList<VigenciaSve>(
    ['cifras', businessId, 'vigencias-sve'],
    businessId ? `/businesses/${businessId}/cifras/sve/vigencias` : undefined,
    {page, search: ''},
    VIGENCIAS_PAGE_SIZE
  )
}

/** Una cifra mueve colores fuera de su pantalla (PQ-4); esta es la única función que sabe que cambió, así que
 * la invalidación vive acá. */
export function useFijarCifra(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      cifraKey,
      valorTexto,
      motivo,
    }: {
      cifraKey: string
      valorTexto: string
      motivo?: string | null
    }) =>
      (
        await api.put<Cifra>(`/businesses/${businessId}/cifras/${cifraKey}`, {
          valor_texto: valorTexto,
          motivo: motivo ?? null,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['cifras', businessId]})
      queryClient.invalidateQueries({queryKey: ['cumplimiento', businessId]})
      queryClient.invalidateQueries({queryKey: ['rejilla-cargo', businessId]})
      queryClient.invalidateQueries({queryKey: ['requisitos-cargo', businessId]})
      // Los días del plazo salen de acá y su fecha de `/cargos-listos`: sin esto la frase se contradice.
      queryClient.invalidateQueries({queryKey: ['solicitudes-ips', businessId]})
      // PQ-8: `vigencia_valoracion_meses` pinta vencimientos en la ficha y en la rejilla.
      queryClient.invalidateQueries({queryKey: ['valoraciones', businessId]})
      queryClient.invalidateQueries({queryKey: ['examenes-matriz', businessId]})
      track('cifra_fijada', {business_id: businessId, cifra_key: variables.cifraKey})
    },
  })
}

/** E10 D8 / S-4: lectura pura; su número viaja en el PUT como constancia de que el firmante lo vio. */
export function useAlcanceSve(businessId: string, positionId: string | null, meses: number | null) {
  return useQuery({
    queryKey: ['cifras', businessId, 'alcance-sve', positionId, meses],
    queryFn: async () =>
      (
        await api.get<AlcanceSve>(`/businesses/${businessId}/cifras/vigencia-sve/${positionId}/alcance`, {
          params: {meses},
        })
      ).data,
    enabled: !!positionId && !!meses,
  })
}

/** Si el cargo cambió desde el `alcanceVistoExamenes` confirmado, la API responde 409. */
export function useFijarVigenciaSve(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {positionId: string; meses: number; sveNombre: string; alcanceVistoExamenes: number}) =>
      (
        await api.put<VigenciaSve>(`/businesses/${businessId}/cifras/vigencia-sve/${input.positionId}`, {
          meses: input.meses,
          sve_nombre: input.sveNombre,
          alcance_visto_examenes: input.alcanceVistoExamenes,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['cifras', businessId]})
      track('vigencia_sve_fijada', {business_id: businessId})
    },
  })
}
